import React, { useState, useEffect } from 'react';
import '../../styles/smart.scss';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers';
import { toast } from 'react-toastify';
import WithdrawConfirmModal from './WithdrawConfirmModal';
import { UserType } from '../../types';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';

const AccountPage = () => {
  const [ donorNo, setDonorNo ] = useState('');
  const [ name, setName ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ passwordConfirm, setPasswordConfirm ] = useState('');
  const [ showWithdrawConfirm, setShowWithdrawConfirm ] = useState(false);
  const { get, post, loading } = useApi();
  const authUser = useAuthUserContext().user;
  const isDonee = authUser?.userType === UserType.Donee;

  useTitle('Ton Bébé | アカウント設定');

  const formatName = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val.slice(0,2).toUpperCase());
  }

  useEffect(() => {
    if (!authUser?.userId) return;

    get({
      endpoint: `api/v1/users/${ authUser.userId }`,
      onSuccess: (res) => {
        setDonorNo(res.data.user_no || '');
        setName(res.data.name || '');
        setEmail(res.data.email || '');
        setPassword('');
        setPasswordConfirm('');
      },
      onError: (err) => {
        toast.error("ユーザー情報の取得に失敗しました。時間をおいてお試しください。");
      },
    });
  }, [get, authUser?.userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser?.userId) return;

    const regInitial = /^[A-Z]{2}$/;
    if (!regInitial.test(name)) {
      toast.error('イニシャルは英字大文字2文字で入力してください');
      return;
    }

    if (password) {
      const regPassword = /^[a-zA-Z0-9\-_@?!$%&]{8,100}$/;
      if (!regPassword.test(password)) {
        toast.error('パスワードは半角英数記号8文字以上100文字以内で入力してください。使用可能記号: -_@?!$%&');
        return;
      }
    }

    if (password !== passwordConfirm) {
      toast.error('パスワードが一致しません');
      return;
    }

    await post({
      endpoint: `api/v1/users/${ authUser.userId }`,
      method: 'PATCH',
      body: {
        name,
        email,
        password,
        password_confirmation: passwordConfirm,
      },
      onSuccess: (res) => {
        // メールアドレス変更時はバックエンドからのメッセージを表示
        const message = res.message || 'アカウント情報を更新しました';
        toast.success(message);
        setPassword('');
        setPasswordConfirm('');
      },
      onError: (err) => {
        toast.error('アカウント情報の更新に失敗しました');
      },
    });
  };

  const handleWithdraw = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowWithdrawConfirm(true);
  };

  return (
    <main className="sub">
      { loading && <Spinner overlay={ true } color={ isDonee ? "pink" : "blue" } /> }
      <section className={ isDonee ? "donee_change pink" : "donor_change" }>
        <h2 className="main_title"><span>アカウント設定</span></h2>
        <h3 className="sub_title">アカウント情報</h3>
        <form onSubmit={ handleSubmit } className="form" autoComplete="off">
          <dl className={ "graf_list " + (isDonee ? "pink" : "") }>
            <dt>{ isDonee ? 'ドニーNo' : 'ドナーNo' }</dt>
            <dd>{ donorNo }</dd>
            <dt>イニシャル</dt>
            <dd>
              <input
                type="text"
                value={ name }
                maxLength={ 2 }
                onChange={ e => setName(e.target.value) }
                onBlur={ e => formatName(e) }
                placeholder="TB"
              />
            </dd>
            <dt>メールアドレス</dt>
            <dd><input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="new-email" /></dd>
            <dt>パスワード</dt>
            <dd><input type="password" value={ password } onChange={ e => setPassword(e.target.value) } autoComplete="new-password" placeholder="半角英数記号8文字以上" /></dd>
            <dt>パスワード（確認）</dt>
            <dd><input type="password" value={ passwordConfirm } onChange={ e => setPasswordConfirm(e.target.value) } placeholder="パスワードを再入力してください" /></dd>
          </dl>
          <input type="submit" value="アカウント情報更新" className={ isDonee ? "w_pinkbtn" : "w_bluebtn" } />
        </form>
        <h3 className="sub_title">退会する</h3>
        <p>退会すると全ての登録情報が削除されますのでご注意ください</p>
        <button type="button" className="orangebtn" onClick={ handleWithdraw }>退会する</button>
        {showWithdrawConfirm && (
          <WithdrawConfirmModal
            hideModal={ () => setShowWithdrawConfirm(false) }
            isDonee={ isDonee }
          />
        )}
      </section>
    </main>
  );
};

export default AccountPage; 
